import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AccountContext } from '../useContext/Account';
import { useMarco, RUTA_INICIO } from './MarcoContext';
import Icono, { iconoCarpeta, nombreCarpeta, sinTildes } from './Icono';
import logo from '../imagenes/logo-marca.png';
import { precargar } from '../../pantallas';

/* Menú lateral. Todo se maneja también con el teclado:
     Alt+M        ir al buscador del menú (escribir filtra todas las carpetas)
     ↑ ↓          moverse            → ←   abrir / cerrar carpeta
     Enter        abrir la pantalla  Esc   volver a la pantalla
     Ctrl+B       contraer / expandir el menú                                      */

const coincide = (texto, consulta) => {
  const t = sinTildes(texto) + ' ' + sinTildes(nombreCarpeta(texto));
  return sinTildes(consulta).split(/\s+/).filter(Boolean).every((p) => t.includes(p));
};

const Sidebar = () => {
  const { logout } = useContext(AccountContext);
  const navigate = useNavigate();
  const {
    usuario, carpetas, cargando, errorMenu, cargarMenu, opcionActual,
    opcionesFavoritas, esFavorito, alternarFavorito, contraido, alternarMenu,
  } = useMarco();

  const [abiertas, setAbiertas] = useState(() => new Set());
  const [filtro, setFiltro] = useState('');
  const navRef = useRef(null);
  const buscarRef = useRef(null);

  // al cambiar de pantalla queda abierta solo su carpeta (el menú no crece sin fin)
  const carpetaActual = opcionActual ? opcionActual.carpeta : null;
  useEffect(() => {
    if (carpetaActual) setAbiertas(new Set([carpetaActual]));
  }, [carpetaActual]);

  // Alt+M (lo lanza AtajosGlobales): enfocar el buscador del menú
  useEffect(() => {
    const enfocar = () => {
      if (contraido) alternarMenu();
      setTimeout(() => buscarRef.current && buscarRef.current.focus(), 30);
    };
    window.addEventListener('sym:enfocar-menu', enfocar);
    return () => window.removeEventListener('sym:enfocar-menu', enfocar);
  }, [contraido, alternarMenu]);

  const filtradas = useMemo(() => {
    if (!filtro.trim()) return carpetas;
    return carpetas
      .map((c) => ({
        ...c,
        opciones: coincide(c.carpeta, filtro) ? c.opciones : (c.opciones || []).filter((o) => coincide(o.nombre, filtro)),
      }))
      .filter((c) => c.opciones && c.opciones.length);
  }, [carpetas, filtro]);

  const alternarCarpeta = (nombre) => {
    if (contraido) { alternarMenu(); setAbiertas(new Set([nombre])); return; }
    setAbiertas((prev) => {
      const sig = new Set(prev);
      if (sig.has(nombre)) sig.delete(nombre); else sig.add(nombre);
      return sig;
    });
  };
  const estaAbierta = (nombre) => !!filtro.trim() || abiertas.has(nombre);

  /* ── teclado dentro del menú ─────────────────────────────────────────── */
  const visibles = () => Array.from(navRef.current ? navRef.current.querySelectorAll('[data-nav]') : [])
    .filter((el) => el.offsetParent !== null);

  const alTeclear = (e) => {
    const el = document.activeElement;
    const items = visibles();
    const i = items.indexOf(el);
    const mover = (j) => { const k = Math.max(0, Math.min(items.length - 1, j)); if (items[k]) items[k].focus(); };
    switch (e.key) {
      case 'ArrowDown': mover(i + 1); break;
      case 'ArrowUp': if (i <= 0) buscarRef.current && buscarRef.current.focus(); else mover(i - 1); break;
      case 'Home': mover(0); break;
      case 'End': mover(items.length - 1); break;
      case 'ArrowRight':
        if (el && el.dataset.carpeta !== undefined) {
          if (!estaAbierta(el.dataset.carpeta)) alternarCarpeta(el.dataset.carpeta); else mover(i + 1);
        } else return;
        break;
      case 'ArrowLeft':
        if (el && el.dataset.carpeta !== undefined) {
          if (estaAbierta(el.dataset.carpeta) && !filtro.trim()) alternarCarpeta(el.dataset.carpeta);
        } else if (el && el.dataset.de) {
          const padre = items.find((x) => x.dataset.carpeta === el.dataset.de);
          if (padre) padre.focus();
        } else return;
        break;
      case 'Escape': {
        const cont = document.querySelector('.sym-contenido');
        if (cont) cont.focus();
        break;
      }
      default:
        // escribir una letra sobre el menú la lleva al buscador
        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey && /\S/.test(e.key) && buscarRef.current) {
          buscarRef.current.focus();
          setFiltro((f) => f + e.key);
          break;
        }
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const alTeclearBuscador = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); e.stopPropagation();
      const items = visibles();
      const primera = items.find((x) => x.dataset.de) || items[0];
      if (primera) primera.focus();
    } else if (e.key === 'Enter') {
      e.preventDefault(); e.stopPropagation();
      const primera = visibles().find((x) => x.dataset.de);
      if (primera) { primera.click(); setFiltro(''); }
    } else if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      if (filtro) setFiltro('');
      else { const cont = document.querySelector('.sym-contenido'); if (cont) cont.focus(); }
    }
  };

  const iniciales = (usuario || 'US').slice(0, 2);

  return (
    <aside className={`sym-lateral${contraido ? ' sym-lateral--contraido' : ''}`} aria-label="Menú principal">
      {/* ── Marca ── */}
      <div className="sym-lateral__marca">
        {!contraido && (
          <NavLink to={RUTA_INICIO} className="sym-lateral__logo" title="Inicio (Alt+0)">
            <img src={logo} alt="Laboratorio Rey-Fals" />
          </NavLink>
        )}
        <button
          type="button"
          className="sym-icono-btn sym-icono-btn--oscuro"
          onClick={alternarMenu}
          title={contraido ? 'Expandir menú (Ctrl+B)' : 'Contraer menú (Ctrl+B)'}
          aria-label={contraido ? 'Expandir menú' : 'Contraer menú'}
          aria-expanded={!contraido}
        >
          <Icono nombre={contraido ? 'expandir' : 'contraer'} tam={18} />
        </button>
      </div>

      {/* ── Buscador del menú ── */}
      {!contraido ? (
        <div className="sym-lateral__buscar">
          <Icono nombre="buscar" tam={15} />
          <input
            ref={buscarRef}
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            onKeyDown={alTeclearBuscador}
            placeholder="Buscar en el menú"
            aria-label="Buscar en el menú"
            autoComplete="off"
            spellCheck={false}
          />
          {filtro
            ? <button type="button" className="sym-lateral__limpiar" onClick={() => { setFiltro(''); buscarRef.current && buscarRef.current.focus(); }} aria-label="Limpiar búsqueda"><Icono nombre="cerrar" tam={13} /></button>
            : <kbd className="sym-kbd sym-kbd--oscuro">Alt M</kbd>}
        </div>
      ) : (
        <button type="button" className="sym-lateral__item sym-lateral__item--solo" onClick={() => window.dispatchEvent(new Event('sym:enfocar-menu'))} title="Buscar en el menú (Alt+M)">
          <Icono nombre="buscar" tam={18} />
        </button>
      )}

      {/* ── Opciones ── */}
      <nav className="sym-lateral__nav" ref={navRef} onKeyDown={alTeclear}>
        {!filtro.trim() && (
          <NavLink to={RUTA_INICIO} end data-nav="" className={({ isActive }) => `sym-lateral__item sym-lateral__inicio${isActive ? ' activo' : ''}`} title={contraido ? 'Inicio (Alt+0)' : undefined}>
            <span className="sym-lateral__ico"><Icono nombre="inicio" tam={18} /></span>
            {!contraido && <span className="sym-lateral__texto">Inicio</span>}
            {!contraido && <kbd className="sym-kbd sym-kbd--oscuro sym-kbd--mini">Alt 0</kbd>}
          </NavLink>
        )}

        {/* favoritos: Alt+1 … Alt+9 */}
        {!filtro.trim() && opcionesFavoritas.length > 0 && (
          <div className="sym-lateral__grupo">
            {!contraido && <div className="sym-lateral__titulo">Favoritos</div>}
            {opcionesFavoritas.map((o, i) => (
              <NavLink key={o.ruta} to={o.ruta} data-nav="" data-de="__favoritos" className={({ isActive }) => `sym-lateral__item${isActive ? ' activo' : ''}`} title={contraido ? `${o.nombre} (Alt+${i + 1})` : undefined}>
                <span className="sym-lateral__ico sym-lateral__ico--estrella"><Icono nombre="estrella" tam={16} /></span>
                {!contraido && <span className="sym-lateral__texto">{o.nombre}</span>}
                {!contraido && <kbd className="sym-kbd sym-kbd--oscuro sym-kbd--mini">Alt {i + 1}</kbd>}
              </NavLink>
            ))}
          </div>
        )}

        {!contraido && !filtro.trim() && carpetas.length > 0 && <div className="sym-lateral__titulo">Módulos</div>}

        {filtradas.map((c) => {
          const abierta = estaAbierta(c.carpeta);
          const activa = opcionActual && opcionActual.carpeta === c.carpeta;
          return (
            <div key={c.carpeta} className={`sym-lateral__carpeta${abierta ? ' abierta' : ''}${activa ? ' activa' : ''}`}>
              <button
                type="button"
                data-nav=""
                data-carpeta={c.carpeta}
                className="sym-lateral__item sym-lateral__item--carpeta"
                onClick={() => alternarCarpeta(c.carpeta)}
                aria-expanded={abierta}
                title={contraido ? nombreCarpeta(c.carpeta) : undefined}
              >
                <span className="sym-lateral__ico"><Icono nombre={iconoCarpeta(c.carpeta)} tam={18} /></span>
                {!contraido && <span className="sym-lateral__texto">{nombreCarpeta(c.carpeta)}</span>}
                {!contraido && <span className="sym-lateral__cuenta">{(c.opciones || []).length}</span>}
                {!contraido && <Icono nombre="flecha" tam={14} className="sym-lateral__chevron" />}
              </button>
              {!contraido && abierta && (
                <div className="sym-lateral__opciones" role="group" aria-label={nombreCarpeta(c.carpeta)}>
                  {(c.opciones || []).map((o) => {
                    const ruta = '/' + String(o.ruta || '').replace(/^\/+/, '');
                    const fav = esFavorito(ruta);
                    return (
                      <div key={ruta} className="sym-lateral__fila">
                        <NavLink to={ruta} data-nav="" data-de={c.carpeta} className={({ isActive }) => `sym-lateral__item sym-lateral__sub${isActive ? ' activo' : ''}`} onClick={() => setFiltro('')}
                          onMouseEnter={() => precargar(ruta)} onFocus={() => precargar(ruta)}>
                          <span className="sym-lateral__punto" />
                          <span className="sym-lateral__texto">{o.nombre}</span>
                        </NavLink>
                        <button
                          type="button"
                          tabIndex={-1}
                          className={`sym-lateral__fav${fav ? ' marcado' : ''}`}
                          onClick={() => alternarFavorito(ruta)}
                          title={fav ? 'Quitar de favoritos' : 'Agregar a favoritos (Alt+1 … Alt+9)'}
                          aria-label={fav ? `Quitar ${o.nombre} de favoritos` : `Agregar ${o.nombre} a favoritos`}
                        >
                          <Icono nombre="estrella" tam={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {!contraido && cargando && carpetas.length === 0 && (
          <div className="sym-lateral__estado"><span className="spinner-border spinner-border-sm me-2" role="status" />Cargando menú…</div>
        )}
        {!contraido && errorMenu && (
          <div className="sym-lateral__estado sym-lateral__estado--error">
            {errorMenu}
            <button type="button" className="btn btn-sm btn-light mt-2 w-100" onClick={cargarMenu}>Reintentar</button>
          </div>
        )}
        {!contraido && filtro.trim() && filtradas.length === 0 && (
          <div className="sym-lateral__estado">Ninguna opción coincide con «{filtro}».</div>
        )}
      </nav>

      {/* ── Pie: usuario y salida ── */}
      <div className="sym-lateral__pie">
        <div className="sym-avatar" title={usuario}>{iniciales}</div>
        {!contraido && (
          <div className="sym-lateral__usuario">
            <strong>{usuario}</strong>
            <span><i className="sym-punto-verde" /> En línea</span>
          </div>
        )}
        <button
          type="button"
          className="sym-icono-btn sym-icono-btn--oscuro"
          onClick={() => { if (window.confirm('¿Cerrar la sesión?')) { logout(); navigate('/'); } }}
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
        >
          <Icono nombre="salir" tam={17} />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
